/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { useLogin } from '../../../../pages/user/login/hooks/useLogin';
import SnackbarMessage from '../../../../pages/user/login/components/SnackbarMessage/SnackbarMessage';
import { Link, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { loginWithGoogle } from '../../services/post/login/login';
import { useAuth } from '../../../../context/AuthContext/AuthContext';
import TwoFactorAuthModal from '../../../../components/TwoFactorAuthModal/TwoFactorAuthModal';
import ThirdPartyAuthButtons from '../../components/ThirdPartyAuthButtons/ThirdPartyAuthButtons';
import OrDivider from '../../components/OrDivider/OrDivider';
import { inputBorderSx } from '../../styles/commonInputStyles';
import { registerColors } from '../../styles/colors';

interface LoginContentProps {
  onTwoFactorRequired?: (email: string, rememberMe: boolean) => void;
}

const LoginContent: React.FC<LoginContentProps> = ({ onTwoFactorRequired }) => {
  const { t } = useTranslation('login');
  // An affiliate or campaign link that lands on the login page keeps its tags for /apply.
  const location = useLocation();
  const { login, loginWithTokens, twoFactorMethods, twoFactorEmail, clearTwoFactorState, setGoogleTwoFactorRequired } = useAuth();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [thirdPartyError, setThirdPartyError] = useState<string | null>(null);
  const [openTwoFactorModal, setOpenTwoFactorModal] = useState(false);

  const {
    email,
    setEmail,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    emailError,
    passwordError,
    formError,
    isSubmitting,
    snackbarMessage,
    clearSnackbarMessage,
    handleLogin,
  } = useLogin(onTwoFactorRequired);

  useEffect(() => {
    if (twoFactorMethods.length > 0) {
      setOpenTwoFactorModal(true);
    } else {
      setOpenTwoFactorModal(false);
    }
  }, [twoFactorMethods]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleLogin();
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await loginWithGoogle(tokenResponse.access_token);
        await loginWithTokens(
          res.data.accessToken,
          res.data.refreshToken || '',
          'google',
        );
      } catch (error: any) {
        console.error('Google login error:', error);
        if (error?.twoFactorRequired && error?.methods) {
          setGoogleTwoFactorRequired(error.email, error.methods);
          setThirdPartyError(null);
        } else if (error?.response?.status === 409) {
          setThirdPartyError(t('ERROR_ACCOUNT_ALREADY_EXISTS'));
        } else {
          setThirdPartyError(t('ERROR_THIRD_PARTY_LOGIN_FAILED'));
        }
      }
    },
    onError: () => {
      setThirdPartyError(t('ERROR_THIRD_PARTY_LOGIN_FAILED'));
    },
  });

  return (
    <Stack width={'100%'} spacing={4}>
      <ThirdPartyAuthButtons
        onGoogleClick={() => handleGoogleLogin()}
        googleButtonText={t('BUTTON_SIGN_UP_WITH_GOOGLE')}
      />

      <OrDivider text={t('TEXT_OR')} />

      <form onSubmit={handleSubmit} style={{ width: '100%' }} aria-label="Login form">
        <Stack gap={1.5}>
          <TextField
            id="email"
            label={t('LABEL_EMAIL_ADDRESS')}
            variant="outlined"
            placeholder={t('PLACEHOLDER_EMAIL_ADDRESS')}
            type="email"
            fullWidth
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(emailError)}
            helperText={emailError || ''}
            sx={inputBorderSx}
          />

          <TextField
            id="Code"
            label={t('LABEL_PASSWORD')}
            placeholder={t('PLACEHOLDER_PASSWORD')}
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={Boolean(passwordError)}
            helperText={passwordError || ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputBorderSx}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mt={0.8}
          mb={4}
        >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{ color: registerColors.checkboxColor }}
                />
              }
              label={t('CHECKBOX_REMEMBER_ME')}
            />
          </FormGroup>
          <Typography
            variant="body1"
            component={Link}
            to={'/forgot-password'}
            state={{ email }}
            sx={{
              textDecoration: 'none',
              color: registerColors.textLink,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('LINK_FORGOT_PASSWORD')}
          </Typography>
        </Stack>

        {(formError || thirdPartyError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError || thirdPartyError}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          sx={{ height: '45px' }}
        >
          {isSubmitting ? <CircularProgress size={24} /> : t('BUTTON_CONTINUE')}
        </Button>

        <Typography
          mt={6}
          variant="body1"
          textAlign={'center'}
          sx={{ color: registerColors.textLink }}
        >
          {t('TEXT_NO_ACCOUNT_YET')}{' '}
          <Typography
            variant="body1"
            component={Link}
            to={{ pathname: '/apply', search: location.search }}
            sx={{ textDecoration: 'underline', color: registerColors.textLink }}
          >
            {t('LINK_CREATE_YOUR_ACCOUNT')}
          </Typography>
        </Typography>
      </form>

      <SnackbarMessage
        open={!!snackbarMessage}
        message={snackbarMessage}
        handleClose={clearSnackbarMessage}
      />
      <TwoFactorAuthModal
        open={openTwoFactorModal}
        email={twoFactorEmail || email}
        availableMethods={twoFactorMethods}
        onVerify={async (method, code) => {
          await login(email, password, rememberMe, method, code);
          setOpenTwoFactorModal(false);
        }}
        onClose={() => {
          setOpenTwoFactorModal(false);
          clearTwoFactorState();
        }}
      />
    </Stack>
  );
};

export default LoginContent;
